<template>
  <div
    class="px-4 sm:px-6 py-3 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-3"
  >
    <HeroSearchBar v-model="searchQuery" />

    <div class="flex items-center gap-2">
      <label class="text-sm text-gray-700 font-medium">Show</label>
      <select
        v-model.number="itemsPerPage"
        @change="handlePerPageChange"
        class="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-ds-button-color"
      >
        <option v-for="option in perPageOptions" :key="option" :value="option">
          {{ option }}
        </option>
      </select>
      <span class="text-sm text-gray-600">entries</span>
    </div>
  </div>

  <div class="overflow-x-auto">
    <table class="w-full text-sm text-gray-700">
      <thead class="sticky top-0 z-10 bg-gray-50 shadow-sm">
        <tr class="text-left text-xs tracking-wider text-gray-600 uppercase">
          <th class="px-4 sm:px-6 py-3">Agent Name</th>
          <th class="px-4 sm:px-6 py-3">Broker</th>
          <th class="px-4 sm:px-6 py-3">Designation</th>
          <th class="px-4 sm:px-6 py-3">Agent Status</th>
          <th class="px-4 sm:px-6 py-3">Card Status</th>
          <th class="px-4 sm:px-6 py-3">Encoded From</th>
          <th class="px-4 sm:px-6 py-3 text-center">Actions</th>
        </tr>
      </thead>

      <tbody v-if="!isLoading && agents.length">
        <tr
          v-for="(agent, index) in agents"
          :key="agent.id || index"
          class="border-b transition last:border-0 odd:bg-white even:bg-gray-50 hover:bg-gray-100"
        >
          <td class="px-4 sm:px-6 py-4 font-semibold text-gray-900">
            {{ toTitleCase(agent.agentname) }}
          </td>

          <td class="px-4 sm:px-6 py-4 font-semibold">
            {{ agent.broker_name ?? "N/A" }}
          </td>

          <td class="px-4 sm:px-6 py-4 font-semibold text-gray-700">
            Account Officer
          </td>

          <td class="px-4 sm:px-6 py-4 font-semibold">
            {{ agent.Status ? "Active" : "Inactive" }}
          </td>

          <td class="px-4 sm:px-6 py-4 font-semibold">
            {{ Number(agent.isPrinted) === 0 ? "-" : "Printed" }}
          </td>

          <td class="px-4 sm:px-6 py-4 font-semibold">
            {{ agent.broker_name ? "Agent Web" : "Local System" }}
          </td>

          <td class="px-4 sm:px-6 py-4 font-semibold text-center">
            <button
              class="rounded-xl bg-ds-button-color px-3 sm:px-5 py-2 text-sm sm:text-base font-semibold text-white shadow-md transition hover:scale-105 active:scale-95"
              @click="openModal(agent)"
            >
              View Details
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="isLoading">
      <div
        v-for="n in 1"
        :key="n"
        class="animate-pulse flex justify-between py-4 border-b p-4"
      >
        <div class="h-4 bg-gray-200 rounded w-1/3"></div>
        <div class="h-4 bg-gray-200 rounded w-1/4"></div>
        <div class="h-4 bg-gray-200 rounded w-1/5"></div>
      </div>
    </div>

    <HeroApplicantDetails
      v-if="showApplicantModal"
      :isVisible="showApplicantModal"
      :applicant="selectedApplicant"
      @close="closeModal"
      @hired="handleHired"
    />

    <HeroAgentDetails
      v-if="showAgentModal"
      :isVisible="showAgentModal"
      :agent="selectedAgent"
      @close="closeModal"
    />

    <HeroSubPagination
      :currentPage="currentPage"
      :totalPages="totalPages"
      @update:page="handlePageChange"
    />
  </div>
</template>

<script>
import { ref, onMounted, inject, computed, watch } from "vue";
import HeroSearchBar from "@/components/common/HeroSearchBar.vue";
import HeroAgentDetails from "@/components/common/HeroAgentDetails.vue";
import HeroApplicantDetails from "@/components/common/HeroApplicantDetails.vue";
import HeroSubPagination from "@/components/common/HeroSubPagination.vue";
import { toTitleCase } from "@/utils/toTitleCase.js";

export default {
  components: {
    HeroSearchBar,
    HeroAgentDetails,
    HeroApplicantDetails,
    HeroSubPagination,
  },

  setup() {
    const axios = inject("$axios");
    const store = inject("$store");

    const searchQuery = ref("");
    const agents = ref([]);
    const isLoading = ref(false);

    const showApplicantModal = ref(false);
    const showAgentModal = ref(false);

    const selectedAgent = ref(null);
    const selectedApplicant = ref(null);

    const currentPage = ref(1);
    const totalItems = ref(0);
    const itemsPerPage = ref(10);
    const perPageOptions = [10, 30, 50];

    const userRole = computed(() => store.state.adminLogDetails.role);
    const name = computed(() => store.state.adminLogDetails.name);

    const totalPages = computed(() =>
      Math.max(1, Math.ceil(totalItems.value / itemsPerPage.value)),
    );

    const fetchAgents = async (page = 1) => {
      isLoading.value = true;
      try {
        const response = await axios.get("/wb/agent", {
          params: {
            page: currentPage.value,
            pageSize: itemsPerPage.value,
            sortBy: "Agentcode",
            sortOrder: "desc",
            search: searchQuery.value.trim(),
          },
        });

        const data = response.data;
        agents.value = data.data || [];
        totalItems.value = data.total ?? data.totalItems ?? agents.value.length;

        currentPage.value =
          typeof data.currentPage === "number" ? data.currentPage : page;
      } catch (err) {
        console.error("Error fetching agents:", err);
      } finally {
        isLoading.value = false;
      }
    };

    const handlePageChange = (page) => {
      if (page !== currentPage.value) {
        currentPage.value = page;
        fetchAgents(page);
      }
    };

    const handlePerPageChange = async () => {
      currentPage.value = 1;
      await fetchAgents(1);
    };

    const openModal = (agent) => {
      selectedAgent.value = agent;
      selectedApplicant.value = null;
      showAgentModal.value = true;
      showApplicantModal.value = false;
    };

    const closeModal = () => {
      showApplicantModal.value = false;
      showAgentModal.value = false;
      selectedAgent.value = null;
      selectedApplicant.value = null;
    };

    const handleHired = () => {
      fetchAgents(currentPage.value);
    };

    let searchTimeout;
    watch(searchQuery, () => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        currentPage.value = 1;
        fetchAgents(1);
      }, 500);
    });

    onMounted(() => {
      fetchAgents(currentPage.value);
    });

    return {
      searchQuery,
      agents,
      isLoading,

      showApplicantModal,
      showAgentModal,
      selectedAgent,
      selectedApplicant,

      currentPage,
      totalPages,
      itemsPerPage,
      perPageOptions,

      fetchAgents,
      handlePageChange,
      handlePerPageChange,

      openModal,
      closeModal,
      handleHired,

      userRole,
      name,
      toTitleCase,
    };
  },
};
</script>
